import { isUsableReturnUrl } from "../utils/buildReturnUrl";

/** Query that METS adds when it sends a supplier to change the Sovra password. */
export const CHANGE_PASSWORD_QUERY = "changePassword";

/** Portal page METS wants the browser sent back to after the password change. */
export const RETURN_URL_QUERY = "return_url";

export function isPasswordChangeArrival(search: string): boolean {
  const query = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(query).get(CHANGE_PASSWORD_QUERY) === "1";
}

/** Path stored for the OIDC callback. The change-password query has to survive the round trip. */
export function authReturnPath(pathname: string, search: string): string {
  return isPasswordChangeArrival(search)
    ? `${pathname}${search.startsWith("?") ? search : `?${search}`}`
    : pathname;
}

/** Token of the fresh sign-in in flight. The callback must hand the same token back as its state. */
export const PASSWORD_CHANGE_REAUTH_KEY = "password_change_reauth";

export type PasswordChangeReauthStep = "redirect" | "complete" | "idle";

/** One token per sign-in attempt, so an abandoned attempt cannot satisfy a later arrival. */
export function newPasswordChangeReauthToken(): string {
  return crypto.randomUUID();
}

/**
 * A change-password arrival always starts one `prompt=login` round trip, including when the SPA
 * already has a session. Only the callback of that round trip returns the expected token as the
 * user's state; oidc-client-ts does not persist that state, so a session restored from storage
 * never matches. `startedHere` keeps a remount of the page that started the redirect idle.
 */
export function passwordChangeReauthStep(input: {
  search: string;
  isAuthenticated: boolean;
  expectedToken: string | null;
  returnedState: unknown;
  startedHere: boolean;
  consumed: boolean;
}): PasswordChangeReauthStep {
  if (!isPasswordChangeArrival(input.search) || input.consumed) {
    return "idle";
  }
  if (
    input.isAuthenticated &&
    !input.startedHere &&
    !!input.expectedToken &&
    input.returnedState === input.expectedToken
  ) {
    return "complete";
  }
  if (input.startedHere) {
    return "idle";
  }
  return "redirect";
}

/** Fresh credentials. An existing Auth0 session must not skip the sign-in. */
export function passwordChangeSigninArgs(
  search: string,
  reauthToken?: string
): { extraQueryParams: { prompt: "login" }; state?: string } | undefined {
  if (!isPasswordChangeArrival(search)) {
    return undefined;
  }
  return reauthToken
    ? { extraQueryParams: { prompt: "login" }, state: reauthToken }
    : { extraQueryParams: { prompt: "login" } };
}

/** Portal return URL from the handoff query, or null when it is missing or not a usable absolute URL. */
export function passwordChangeReturnUrl(search: string): string | null {
  const query = search.startsWith("?") ? search.slice(1) : search;
  const value = new URLSearchParams(query).get(RETURN_URL_QUERY);
  return value && isUsableReturnUrl(value) ? value : null;
}

/** Where to send the browser after Identity accepts the change. Null keeps the supplier on this page. */
export function passwordChangeLanding(
  redirectUrl: string | null | undefined
): string | null {
  return redirectUrl && isUsableReturnUrl(redirectUrl) ? redirectUrl : null;
}
